import { useEffect, useRef, useState } from "react";
import "@/App.css";
import {
  ArrowDown,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Feather,
  FileText,
  Gavel,
  Globe,
  GraduationCap,
  Instagram,
  Layers,
  Mail,
  Microscope,
  PenLine,
  TrendingUp,
  UserRound,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────────
   FORMSPREE SETUP — read me before going live
   1. Create a free account at https://formspree.io (free plan is enough).
   2. Click "+ New form", name it e.g. "Marginalia Waitlist".
   3. Set the target/notification email to shivharpale01@gmail.com — every
      waitlist submission will be emailed there automatically.
   4. Formspree gives you a form ID like "xrgnewab". Paste it below,
      replacing YOUR_FORM_ID. Nothing else needs to change.
   ──────────────────────────────────────────────────────────────────────────── */
const FORMSPREE_FORM_ID = "YOUR_FORM_ID"; // ← PASTE YOUR FORMSPREE FORM ID HERE
const FORMSPREE_ENDPOINT = `https://formspree.io/f/${FORMSPREE_FORM_ID}`;

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1723220217596-45d4b51e2804?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxhbm5vdGF0ZWQlMjBib29rJTIwaGFuZHdyaXR0ZW4lMjBub3RlcyUyMGxpYnJhcnklMjByZWFkaW5nJTIwdmludGFnZSUyMGJvb2tzfGVufDB8fHx8MTc4ODE5NDQ5NHww&ixlib=rb-4.1.0&q=80&w=1200&auto=format";

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Reveal = ({ children, className = "", delay = 0, ...props }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "revealed" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </div>
  );
};

const Kicker = ({ children }) => (
  <p className="font-sans text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-sepia">
    {children}
  </p>
);

/* ── Header ─────────────────────────────────────────────────────────────── */
const Header = () => (
  <header
    data-testid="nav-header"
    className="sticky top-0 z-50 border-b border-[#E5DECE] bg-paper/85 backdrop-blur-md"
  >
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
      <a href="#top" data-testid="brand-logo" className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E5DECE] bg-white shadow-sm">
          <Feather className="h-4.5 w-4.5 text-ink" strokeWidth={2.2} />
        </span>
        <span className="font-serif text-xl font-bold tracking-tight text-charcoal">Marginalia</span>
      </a>
      <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
        {[
          ["How it works", "how-it-works"],
          ["Why it's different", "why-different"],
          ["Categories", "categories"],
          ["For annotators", "for-annotators"],
        ].map(([label, id]) => (
          <button
            key={id}
            type="button"
            data-testid={`nav-link-${id}`}
            onClick={() => scrollToId(id)}
            className="text-sm font-medium text-charcoal/70 transition-colors duration-200 hover:text-ink"
          >
            {label}
          </button>
        ))}
      </nav>
      <button
        type="button"
        data-testid="nav-waitlist-button"
        onClick={() => scrollToId("waitlist")}
        className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-ink-deep hover:shadow-md active:translate-y-0"
      >
        Join the waitlist
      </button>
    </div>
  </header>
);

/* ── Hero ───────────────────────────────────────────────────────────────── */
const Hero = () => (
  <section id="top" data-testid="hero-section" className="paper-texture relative overflow-hidden">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-20 pt-14 sm:px-8 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:pb-28 lg:pt-24">
      <div>
        <Reveal>
          <Kicker>A marketplace for hand-annotated books</Kicker>
        </Reveal>
        <Reveal delay={80}>
          <h1
            data-testid="hero-heading"
            className="mt-5 font-serif text-4xl font-bold leading-[1.12] tracking-tight text-charcoal sm:text-5xl lg:text-6xl"
          >
            Real people's handwritten notes on the books they{" "}
            <span className="ink-underline italic">loved</span>.
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p
            data-testid="hero-subheading"
            className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/80 sm:text-lg"
          >
            Marginalia is where readers sell the notes, underlines, and margin commentary from books
            they've actually read — as one-of-a-kind annotated copies, limited hand-annotated
            editions, and digital companion guides.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              data-testid="hero-cta-waitlist-button"
              onClick={() => scrollToId("waitlist")}
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-semibold text-paper shadow-[0_6px_20px_-6px_rgba(37,99,235,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-ink-deep hover:shadow-[0_10px_28px_-6px_rgba(37,99,235,0.55)] active:translate-y-0"
            >
              Join the waitlist
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>
            <button
              type="button"
              data-testid="hero-cta-how-button"
              onClick={() => scrollToId("how-it-works")}
              className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 bg-white/70 px-7 py-3.5 text-base font-semibold text-charcoal transition-all duration-200 hover:-translate-y-0.5 hover:border-ink/40 hover:text-ink active:translate-y-0"
            >
              How it works
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
        <Reveal delay={320}>
          <p className="mt-8 font-hand text-xl font-bold text-sepia/90 -rotate-1 sm:text-2xl">
            “the good parts, marked by a real person”
          </p>
        </Reveal>
      </div>

      <Reveal delay={200} className="relative">
        <div className="relative mx-auto max-w-md lg:max-w-none">
          <div className="absolute -left-3 -top-3 h-full w-full rotate-[-2deg] rounded-2xl border border-[#E5DECE] bg-paper-deep" />
          <img
            data-testid="hero-image"
            src={HERO_IMAGE}
            alt="An open book covered in handwritten margin notes and underlines"
            loading="eager"
            className="relative w-full rotate-[1.2deg] rounded-2xl border border-[#E5DECE] object-cover shadow-[0_20px_50px_-16px_rgba(31,41,55,0.28)] transition-transform duration-500 hover:rotate-0"
          />
          <div className="absolute -right-3 top-6 rotate-2 rounded-sm border-2 border-dashed border-sepia bg-paper/95 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-sepia shadow-sm sm:-right-5">
            1 of 1 — annotated
          </div>
          <p className="absolute -bottom-5 left-6 -rotate-2 rounded-md bg-white px-4 py-2 font-hand text-lg font-bold text-ink shadow-md sm:text-xl">
            this chapter changed how I work ↓
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ── Problem ────────────────────────────────────────────────────────────── */
const Problem = () => (
  <section data-testid="problem-section" className="border-y border-[#E5DECE] bg-paper-deep/60">
    <div className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-28">
      <Reveal>
        <Kicker>The problem</Kicker>
        <h2
          data-testid="problem-heading"
          className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight text-charcoal sm:text-4xl"
        >
          You didn't stop loving books. You ran out of hours.
        </h2>
      </Reveal>
      <Reveal delay={120}>
        <div className="mt-8 space-y-6 border-l-2 border-sepia/40 pl-6 text-base leading-relaxed text-charcoal/85 sm:pl-8 sm:text-lg">
          <p>
            Somewhere between the to-read pile and the to-do list, reading got expensive. Not in
            money — in time. A good book asks for twenty unhurried hours that most of us no longer
            have, so it sits on the shelf, spine uncracked, full of good intentions.
          </p>
          <p>
            Here's the uncomfortable truth: most of a book's real value lives in a small fraction of
            its pages. The one chapter that changes how you work. The three paragraphs you'd have
            underlined twice. The rest is scaffolding.
          </p>
          <p>
            And someone has usually already found those pages. They read the whole thing, pen in
            hand, and marked exactly what mattered — in the margins of their own copy. Until now,
            that quiet, private work stayed private. Marginalia makes it something you can hold.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ── How it works ───────────────────────────────────────────────────────── */
const FORMATS = [
  {
    icon: Gavel,
    testId: "how-card-physical-auctions",
    title: "Physical Auctions",
    body: "The actual, one-of-a-kind annotated copy — ink, dog-ears and all — sold once, to the highest bidder. When it's gone, it's gone forever.",
    note: "there is exactly one of these",
  },
  {
    icon: Layers,
    testId: "how-card-limited-editions",
    title: "Limited Editions",
    body: "Small, numbered runs from popular annotators. Their handwritten notes, faithfully reproduced, in a short print run you'll actually own.",
    note: "numbered, like good prints",
  },
  {
    icon: FileText,
    testId: "how-card-digital-guides",
    title: "Digital Companion Guides",
    body: "The annotator's original commentary as a digital guide — read alongside the book, on any device, in the time you actually have.",
    note: "read it tonight",
  },
];

const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="scroll-mt-20">
    <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
      <Reveal className="max-w-2xl">
        <Kicker>How it works</Kicker>
        <h2 className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight text-charcoal sm:text-4xl">
          Three ways to read through someone else's eyes.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8">
        {FORMATS.map(({ icon: Icon, testId, title, body, note }, i) => (
          <Reveal key={testId} delay={i * 120}>
            <article
              data-testid={testId}
              className="group relative h-full rounded-2xl border border-[#E5DECE] bg-white p-7 shadow-[0_4px_20px_-2px_rgba(31,41,55,0.06)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_36px_-8px_rgba(31,41,55,0.14)] sm:p-8"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-paper-deep text-ink transition-colors duration-300 group-hover:bg-ink group-hover:text-paper">
                <Icon className="h-5.5 w-5.5" strokeWidth={1.9} />
              </span>
              <h3 className="mt-6 font-serif text-xl font-semibold text-charcoal sm:text-2xl">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/75 sm:text-base">{body}</p>
              <p className="mt-5 font-hand text-lg font-bold text-sepia/80 -rotate-1">{note}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ── Why it's different ─────────────────────────────────────────────────── */
const PILLARS = [
  {
    icon: PenLine,
    testId: "why-feature-authenticity",
    title: "Authenticity",
    body: "Every note was written by a real person in a real moment of reading — at 2 a.m. before an exam, on a train, in a hospital waiting room. Not generated to please an algorithm.",
  },
  {
    icon: BookOpen,
    testId: "why-feature-scarcity",
    title: "Scarcity",
    body: "There is exactly one original annotated copy of anything, and limited runs stay limited. Scarcity here isn't a marketing trick — it's simply the nature of ink.",
  },
  {
    icon: UserRound,
    testId: "why-feature-human-understanding",
    title: "Human understanding",
    body: "An annotator brings their whole life to the page: a UPSC topper's exam instincts, a founder's hard-won shortcuts, a novelist's eye for structure. That's not a summary. That's a lens.",
  },
];

const WhyDifferent = () => (
  <section
    id="why-different"
    data-testid="why-different-section"
    className="scroll-mt-20 border-y border-[#E5DECE] bg-charcoal text-paper"
  >
    <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
      <Reveal className="max-w-3xl">
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.22em] text-[#E8B878] sm:text-sm">
          Why it's different
        </p>
        <h2
          data-testid="why-different-heading"
          className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl"
        >
          A summary tells you what happened.{" "}
          <span className="italic text-[#E8B878]">A margin tells you what mattered.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-paper/70 sm:text-lg">
          AI can compress a book. What it can't do is tell you which line made someone stop
          mid-sentence and stare out of the window — or why a topper circled page 112 three times
          the night before the exam.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
        {PILLARS.map(({ icon: Icon, testId, title, body }, i) => (
          <Reveal key={testId} delay={i * 120}>
            <div data-testid={testId} className="border-t border-paper/15 pt-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E8B878]/40 text-[#E8B878]">
                <Icon className="h-5 w-5" strokeWidth={1.9} />
              </span>
              <h3 className="mt-5 font-serif text-xl font-semibold sm:text-2xl">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-paper/65 sm:text-base">{body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ── Categories ─────────────────────────────────────────────────────────── */
const CATEGORIES = [
  { icon: BookOpen, testId: "category-card-fiction", title: "Fiction", sub: "Novels & literary classics" },
  { icon: TrendingUp, testId: "category-card-self-help-business", title: "Self-Help & Business", sub: "Habits, money, management" },
  { icon: UserRound, testId: "category-card-biography", title: "Biography", sub: "Lives worth underlining" },
  { icon: GraduationCap, testId: "category-card-exam-prep", title: "Exam Prep", sub: "UPSC · JEE · NEET · MPSC · Boards" },
  { icon: Microscope, testId: "category-card-phd-masters", title: "PhD & Master's Entrance", sub: "Research & postgrad admissions" },
  { icon: Globe, testId: "category-card-global-entrance", title: "Global Entrance Exams", sub: "SAT · GRE · GMAT & more" },
];

const Categories = () => (
  <section id="categories" data-testid="categories-section" className="scroll-mt-20">
    <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
      <Reveal className="max-w-2xl">
        <Kicker>Categories</Kicker>
        <h2 className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight text-charcoal sm:text-4xl">
          Every shelf of the bookshop.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-charcoal/75 sm:text-lg">
          If people read it with a pen in their hand, it belongs here.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CATEGORIES.map(({ icon: Icon, testId, title, sub }, i) => (
          <Reveal key={testId} delay={(i % 3) * 100}>
            <div
              data-testid={testId}
              className="group flex h-full items-start gap-4 rounded-xl border border-[#E5DECE] bg-white/70 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-ink/30 hover:bg-white hover:shadow-[0_12px_28px_-8px_rgba(31,41,55,0.12)]"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-paper-deep text-sepia transition-colors duration-300 group-hover:bg-sepia group-hover:text-white">
                <Icon className="h-5 w-5" strokeWidth={1.9} />
              </span>
              <div>
                <h3 className="font-serif text-lg font-semibold text-charcoal">{title}</h3>
                <p className="mt-1 text-sm text-charcoal/65">{sub}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ── For annotators ─────────────────────────────────────────────────────── */
const ForAnnotators = () => (
  <section
    id="for-annotators"
    data-testid="for-annotators-section"
    className="paper-texture scroll-mt-20 border-y border-[#E5DECE] bg-paper-deep/60"
  >
    <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1fr_auto] lg:gap-16">
      <Reveal>
        <Kicker>For annotators</Kicker>
        <h2
          data-testid="for-annotators-heading"
          className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight text-charcoal sm:text-4xl"
        >
          Turn the notes you already make into income.
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal/80 sm:text-lg">
          You already read with a pen in your hand. If you're a topper, a teacher, a founder, or
          simply the friend everyone asks “so what should I take from this book?” — your margins
          have value. Auction the original once, print a limited run, or keep earning from a
          digital guide. You set the format; your notes do the rest.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button
            type="button"
            data-testid="annotators-cta-button"
            onClick={() => scrollToId("waitlist")}
            className="group inline-flex items-center gap-2 rounded-full bg-sepia px-7 py-3.5 text-base font-semibold text-white shadow-[0_6px_20px_-6px_rgba(180,83,9,0.45)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#92400E] active:translate-y-0"
          >
            Join as an annotator
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
          <p className="text-sm text-charcoal/60">
            Annotators and readers join the same waitlist — just tell us which you are.
          </p>
        </div>
      </Reveal>
      <Reveal delay={150} className="hidden lg:block">
        <div className="rotate-2 rounded-xl border border-[#E5DECE] bg-white p-6 shadow-[0_14px_34px_-10px_rgba(31,41,55,0.16)]">
          <p className="font-hand text-2xl font-bold leading-snug text-ink">
            “sold my annotated
            <br />
            Atomic Habits for
            <br />
            3× the cover price”
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-charcoal/50">
            — a future annotator
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ── Waitlist ───────────────────────────────────────────────────────────── */
const INTERESTS = ["Buyer", "Annotator", "Both"];

const Waitlist = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [book, setBook] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name, email, interest, book }),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="scroll-mt-20">
      <div className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-28">
        <Reveal className="text-center">
          <Kicker>The waitlist</Kicker>
          <h2
            data-testid="waitlist-heading"
            className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight text-charcoal sm:text-4xl"
          >
            Be the first to know when we launch.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-charcoal/75 sm:text-lg">
            We're opening the doors shelf by shelf. Leave your name and we'll write to you before
            anyone else — whether you're here to read or to annotate.
          </p>
        </Reveal>

        <Reveal delay={140}>
          {status === "success" ? (
            <div
              data-testid="waitlist-success-container"
              className="mt-12 rounded-2xl border border-ink/25 bg-white p-8 text-center shadow-[0_16px_40px_-12px_rgba(37,99,235,0.18)] sm:p-12"
            >
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ink/10">
                <CheckCircle2 className="h-7 w-7 text-ink" />
              </span>
              <h3 className="mt-5 font-serif text-2xl font-semibold text-charcoal sm:text-3xl">
                You're on the list.
              </h3>
              <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-charcoal/75">
                We'll write to you at <span className="font-semibold text-charcoal">{email}</span>{" "}
                the moment the first annotated copies go up. Until then — keep a pen nearby.
              </p>
              <p className="mt-6 font-hand text-xl font-bold text-sepia -rotate-1">
                see you in the margins ✎
              </p>
            </div>
          ) : (
            <form
              data-testid="waitlist-form"
              onSubmit={handleSubmit}
              className="mt-12 rounded-2xl border border-[#E5DECE] bg-white p-6 shadow-[0_10px_36px_-12px_rgba(31,41,55,0.12)] sm:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="waitlist-name"
                    className="mb-2 block text-sm font-semibold text-charcoal"
                  >
                    Full name <span className="text-sepia">*</span>
                  </label>
                  <input
                    id="waitlist-name"
                    data-testid="waitlist-input-name"
                    name="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Asha Verma"
                    className="w-full rounded-lg border border-[#E5DECE] bg-paper/60 px-4 py-3 text-base text-charcoal placeholder:text-charcoal/35 outline-none transition-colors duration-200 focus:border-ink focus:bg-white focus:ring-2 focus:ring-ink/15"
                  />
                </div>
                <div>
                  <label
                    htmlFor="waitlist-email"
                    className="mb-2 block text-sm font-semibold text-charcoal"
                  >
                    Email <span className="text-sepia">*</span>
                  </label>
                  <input
                    id="waitlist-email"
                    data-testid="waitlist-input-email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-[#E5DECE] bg-paper/60 px-4 py-3 text-base text-charcoal placeholder:text-charcoal/35 outline-none transition-colors duration-200 focus:border-ink focus:bg-white focus:ring-2 focus:ring-ink/15"
                  />
                </div>
              </div>

              <fieldset className="mt-6">
                <legend className="mb-2 text-sm font-semibold text-charcoal">
                  I'm interested as <span className="text-sepia">*</span>
                </legend>
                <div className="flex flex-wrap gap-3">
                  {INTERESTS.map((option) => (
                    <label
                      key={option}
                      data-testid={`waitlist-radio-${option.toLowerCase()}`}
                      className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                        interest === option
                          ? "border-ink bg-ink text-paper shadow-sm"
                          : "border-[#E5DECE] bg-paper/60 text-charcoal/75 hover:border-ink/40 hover:text-ink"
                      }`}
                    >
                      <input
                        type="radio"
                        name="interest"
                        value={option}
                        required
                        checked={interest === option}
                        onChange={() => setInterest(option)}
                        className="sr-only"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="mt-6">
                <label
                  htmlFor="waitlist-book"
                  className="mb-2 block text-sm font-semibold text-charcoal"
                >
                  What book would you want annotated notes for?{" "}
                  <span className="font-normal text-charcoal/50">(optional)</span>
                </label>
                <input
                  id="waitlist-book"
                  data-testid="waitlist-input-book"
                  name="book"
                  type="text"
                  value={book}
                  onChange={(e) => setBook(e.target.value)}
                  placeholder="e.g. The Psychology of Money"
                  className="w-full rounded-lg border border-[#E5DECE] bg-paper/60 px-4 py-3 text-base text-charcoal placeholder:text-charcoal/35 outline-none transition-colors duration-200 focus:border-ink focus:bg-white focus:ring-2 focus:ring-ink/15"
                />
              </div>

              {status === "error" && (
                <p
                  data-testid="waitlist-error-message"
                  role="alert"
                  className="mt-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  Something went wrong on our end — your spot wasn't saved. Please try again in a
                  moment, or write to us directly at shivharpale01@gmail.com.
                </p>
              )}

              <button
                type="submit"
                data-testid="waitlist-submit-button"
                disabled={status === "submitting"}
                className="mt-8 w-full rounded-full bg-ink px-7 py-4 text-base font-semibold text-paper shadow-[0_6px_20px_-6px_rgba(37,99,235,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-ink-deep hover:shadow-[0_10px_28px_-6px_rgba(37,99,235,0.55)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {status === "submitting" ? "Saving your spot…" : "Save my spot"}
              </button>
              <p className="mt-4 text-center text-xs text-charcoal/50">
                No spam, ever. One email when we launch — that's the deal.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

/* ── Footer ─────────────────────────────────────────────────────────────── */
const Footer = () => (
  <footer data-testid="main-footer" className="border-t border-[#E5DECE] bg-paper-deep/60">
    <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
      <div className="flex flex-col items-start justify-between gap-10 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E5DECE] bg-white shadow-sm">
              <Feather className="h-4.5 w-4.5 text-ink" strokeWidth={2.2} />
            </span>
            <span className="font-serif text-xl font-bold tracking-tight text-charcoal">
              Marginalia
            </span>
          </div>
          <p
            data-testid="footer-tagline"
            className="mt-3 font-hand text-2xl font-bold text-sepia -rotate-1"
          >
            Marginalia is the point.
          </p>
        </div>
        <div className="flex flex-col gap-3 text-sm text-charcoal/70">
          <a
            data-testid="footer-contact-email"
            href="mailto:shivharpale01@gmail.com"
            className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-ink"
          >
            <Mail className="h-4 w-4" />
            shivharpale01@gmail.com
          </a>
          {/* TODO: replace "#" with your real Instagram URL once the handle is live */}
          <a
            data-testid="footer-instagram-link"
            href="#"
            className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-ink"
          >
            <Instagram className="h-4 w-4" />
            Instagram — coming soon
          </a>
        </div>
      </div>
      <div className="mt-10 border-t border-[#E5DECE] pt-6 text-xs text-charcoal/50">
        © 2026 Marginalia. All rights reserved.
      </div>
    </div>
  </footer>
);

export default function App() {
  return (
    <div className="min-h-screen bg-paper text-charcoal">
      <Header />
      <main>
        <Hero />
        <Problem />
        <HowItWorks />
        <WhyDifferent />
        <Categories />
        <ForAnnotators />
        <Waitlist />
      </main>
      <Footer />
    </div>
  );
}
